import { useState } from 'react';
import {
  Stack, Button, Chip, TextField, MenuItem, Typography, Alert, Snackbar, Paper, Box, CircularProgress, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Sync, PowerSettingsNew, Bolt } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { amCmsApi, amCmsConnectionsApi, type AmReconciliation } from '../../api/amCms.api';
import { amStationsApi } from '../../api/amStations.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const kwh = (n?: number | null) => (n == null ? '—' : `${Number(n).toLocaleString('en-IN')} kWh`);
const human = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '—');
const liveColor: Record<string, 'success' | 'default' | 'error' | 'warning'> = { online: 'success', offline: 'default', faulty: 'error', unknown: 'warning' };
const reconColor: Record<string, 'success' | 'error' | 'info'> = { good_to_go: 'success', flagged: 'error', verified: 'info' };
const reconLabel: Record<string, string> = { good_to_go: 'Good to Go', flagged: 'Flagged', verified: 'Verified' };

export function CmsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('am_electricity.update');
  const [stationId, setStationId] = useState<number | ''>('');
  const [toast, setToast] = useState<string | null>(null);
  const [verifyRow, setVerifyRow] = useState<AmReconciliation | null>(null);

  const stations = useQuery({ queryKey: ['am-stations', 'options'], queryFn: () => amStationsApi.list({ limit: 500, sortBy: 'stationName', sortDir: 'asc' }) });
  const connections = useQuery({ queryKey: ['cms-connections', 'options'], queryFn: () => amCmsConnectionsApi.list({ limit: 200, status: 'active' }) });
  const status = useQuery({ queryKey: ['cms-status', stationId], enabled: Boolean(stationId), queryFn: () => amCmsApi.status(Number(stationId)) });
  const consumption = useQuery({ queryKey: ['cms-consumption', stationId], enabled: Boolean(stationId), queryFn: () => amCmsApi.consumption(Number(stationId)) });
  const recons = useQuery({ queryKey: ['cms-recons', stationId], queryFn: () => amCmsApi.reconciliations({ limit: 50, stationId: stationId || undefined }) });

  const notConfigured = status.data?.configured === false || consumption.data?.configured === false;

  const syncMut = useMutation({ mutationFn: () => amCmsApi.sync(stationId ? Number(stationId) : undefined), onSuccess: (r) => { setToast(r.configured ? `Synced ${r.synced} charger(s) from ${r.provider}` : 'Site is not mapped to a CMS connection'); qc.invalidateQueries({ queryKey: ['cms-status'] }); }, onError: (e) => setToast(extractApiError(e)) });
  const mapMut = useMutation({ mutationFn: (connectionId: number | null) => amCmsConnectionsApi.map(Number(stationId), connectionId), onSuccess: () => { setToast('CMS mapping updated'); qc.invalidateQueries({ queryKey: ['cms-status'] }); qc.invalidateQueries({ queryKey: ['cms-consumption'] }); }, onError: (e) => setToast(extractApiError(e)) });
  const verifyMut = useMutation({ mutationFn: (v: { id: number; remarks: string }) => amCmsApi.verify(v.id, v.remarks), onSuccess: () => { setVerifyRow(null); setToast('Reconciliation verified'); qc.invalidateQueries({ queryKey: ['cms-recons'] }); }, onError: (e) => setToast(extractApiError(e)) });

  const tiles: { label: string; value: number | undefined }[] = [
    { label: 'Daily', value: consumption.data?.daily }, { label: 'Monthly', value: consumption.data?.monthly }, { label: 'YTD', value: consumption.data?.ytd },
  ];

  return (
    <>
      <PageHeader title="Charger CMS" subtitle="Live charger status, energy consumption (Daily / Monthly / YTD) and bill reconciliation from the connected CMS."
        action={canManage ? <Button startIcon={syncMut.isPending ? <CircularProgress size={16} /> : <Sync />} variant="contained" disabled={syncMut.isPending} onClick={() => syncMut.mutate()}>Sync CMS</Button> : undefined} />

      {stationId && notConfigured && <Alert severity="warning" sx={{ mb: 2 }}>This site is not mapped to a CMS connection. Add one under <strong>CMS Connections</strong>, then pick it in “Mapped CMS” above to enable live data.</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Site" value={stationId} onChange={(e) => setStationId(e.target.value ? Number(e.target.value) : '')} sx={{ minWidth: 260 }}>
          <MenuItem value="">— Select a site —</MenuItem>
          {(stations.data?.data ?? []).map((s) => <MenuItem key={s.id} value={s.id}>{s.stationName}{s.city ? ` — ${s.city}` : ''}</MenuItem>)}
        </TextField>
        {stationId && canManage && (
          <TextField select size="small" label="Mapped CMS" value={status.data?.connectionId ?? ''} onChange={(e) => mapMut.mutate(e.target.value ? Number(e.target.value) : null)} sx={{ minWidth: 240 }}
            helperText="Which CMS this site uses">
            <MenuItem value="">— Not mapped —</MenuItem>
            {(connections.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.name} ({c.provider})</MenuItem>)}
          </TextField>
        )}
      </Stack>

      {stationId ? (
        <Stack spacing={3}>
          {/* Consumption tiles */}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {tiles.map((t) => (
              <Paper key={t.label} sx={{ p: 2, minWidth: 180, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.label} consumption</Typography>
                <Typography variant="h5" sx={{ mt: 0.5 }}><Bolt fontSize="small" sx={{ verticalAlign: 'middle', color: 'warning.main' }} /> {kwh(t.value)}</Typography>
              </Paper>
            ))}
          </Stack>

          {/* Live charger status */}
          <Box>
            <Divider textAlign="left" sx={{ mb: 1 }}><Typography variant="caption" color="text.secondary">Charger live status</Typography></Divider>
            <Table size="small">
              <TableHead><TableRow><TableCell>Charger</TableCell><TableCell>Type</TableCell><TableCell align="right">kW</TableCell><TableCell>Live</TableCell><TableCell>Last heartbeat</TableCell></TableRow></TableHead>
              <TableBody>
                {(status.data?.chargers ?? []).map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.serialNumber ?? c.cmsChargerId ?? `#${c.id}`}</TableCell>
                    <TableCell>{c.chargerType ?? '—'}</TableCell>
                    <TableCell align="right">{c.capacityKw || '—'}</TableCell>
                    <TableCell><Chip size="small" icon={<PowerSettingsNew />} color={liveColor[c.liveStatus]} label={human(c.liveStatus)} /></TableCell>
                    <TableCell>{c.lastHeartbeatAt ? new Date(c.lastHeartbeatAt).toLocaleString('en-IN') : '—'}</TableCell>
                  </TableRow>
                ))}
                {(status.data?.chargers ?? []).length === 0 && <TableRow><TableCell colSpan={5}><Typography variant="caption" color="text.secondary">No chargers on this site.</Typography></TableCell></TableRow>}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      ) : <Typography variant="body2" color="text.secondary">Select a site to see live status & consumption.</Typography>}

      {/* Reconciliation */}
      <Divider textAlign="left" sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Bill reconciliation — CMS units vs electricity bill (±{recons.data?.data[0]?.thresholdPercent ?? 10}%)</Typography></Divider>
      <Table size="small">
        <TableHead><TableRow><TableCell>Site</TableCell><TableCell>Month</TableCell><TableCell align="right">Bill units</TableCell><TableCell align="right">CMS units</TableCell><TableCell align="right">Variance</TableCell><TableCell>Result</TableCell><TableCell /></TableRow></TableHead>
        <TableBody>
          {(recons.data?.data ?? []).map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.stationName ?? `#${r.stationId}`}</TableCell>
              <TableCell>{r.monthYear ? new Date(r.monthYear).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</TableCell>
              <TableCell align="right">{kwh(r.billUnits)}</TableCell>
              <TableCell align="right">{kwh(r.cmsUnits)}</TableCell>
              <TableCell align="right">{r.variancePercent}%</TableCell>
              <TableCell><Chip size="small" color={reconColor[r.status]} label={reconLabel[r.status]} />{r.remarks ? <Typography variant="caption" color="text.secondary" display="block">{r.remarks}</Typography> : null}</TableCell>
              <TableCell align="right">{r.status === 'flagged' && canManage && <Button size="small" onClick={() => setVerifyRow(r)}>Verify</Button>}</TableCell>
            </TableRow>
          ))}
          {(recons.data?.data ?? []).length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="caption" color="text.secondary">No reconciliations yet. They are created automatically each day (and can be run per bill).</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>

      {verifyRow && <VerifyDialog row={verifyRow} onClose={() => setVerifyRow(null)} onSubmit={(remarks) => verifyMut.mutate({ id: verifyRow.id, remarks })} pending={verifyMut.isPending} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function VerifyDialog({ row, onClose, onSubmit, pending }: { row: AmReconciliation; onClose: () => void; onSubmit: (remarks: string) => void; pending: boolean }) {
  const [remarks, setRemarks] = useState('');
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
      <DialogTitle>Verify flagged reconciliation</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>{row.stationName} · {row.monthYear ? new Date(row.monthYear).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : ''} — bill {kwh(row.billUnits)} vs CMS {kwh(row.cmsUnits)} (variance <strong>{row.variancePercent}%</strong>).</Typography>
        <TextField label="Verification remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} sx={{ mt: 1 }} placeholder="e.g. Checked meter — bill correct; CMS gap due to charger offline period." />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={pending} onClick={() => onSubmit(remarks)}>Mark Verified</Button>
      </DialogActions>
    </Dialog>
  );
}
